import React, { useState } from 'react';
import { Modal, Button, Search } from '@/components/ui';
import { Zap, Activity, Info, ChevronRight, Plus, Target, Bell, Briefcase, AlertTriangle, Cpu } from 'lucide-react';
import type { AutomationRule } from '../types/automation.types';

interface RuleBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rule: Partial<AutomationRule>) => void;
  initialData?: Partial<AutomationRule>;
  triggerLabels: Record<string, string>;
  actionLabels: Record<string, string>;
}

export const RuleBuilder: React.FC<RuleBuilderProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  triggerLabels,
  actionLabels,
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<AutomationRule>>(initialData || {
    name: '',
    description: '',
    trigger_type: '',
    action_type: '',
    is_active: true
  });

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const triggerIcons: Record<string, any> = {
    schedule_due: Clock,
    work_order_overdue: AlertTriangle,
    qa_failed: Target,
    issue_created: Bell,
    inventory_low: Briefcase,
    equipment_service_due: Cpu,
  };

  const actionIcons: Record<string, any> = {
    create_work_order: Briefcase,
    send_notification: Bell,
    create_issue: AlertTriangle,
    escalate_to_manager: Target,
    assign_team: Users,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData?.id ? "Chỉnh sửa quy tắc" : "Thiết lập quy tắc mới"}
      size="lg"
    >
      <div className="py-2">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-10">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black transition-all duration-500 shadow-sm ${step >= s ? 'bg-blue-600 text-white scale-110 shadow-blue-200' : 'bg-slate-50 text-slate-300'}`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 rounded-full transition-all duration-1000 ${step > s ? 'bg-blue-600' : 'bg-slate-100'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <label className="text-[11px] font-black text-slate-400  tracking-[0.15em] ml-1">Tên quy tắc *</label>
                <input
                  type="text"
                  className="w-full h-14 px-5 rounded-[20px] border-slate-100 bg-slate-50/50 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-slate-900"
                  placeholder="Ví dụ: Tự động tạo Work Order khi Inventory thấp"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[11px] font-black text-slate-400  tracking-[0.15em] ml-1">Mô tả (Tùy chọn)</label>
                <textarea
                  className="w-full p-5 rounded-[20px] border-slate-100 bg-slate-50/50 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-slate-900 min-h-[120px]"
                  placeholder="Mô tả chi tiết mục đích và điều kiện của quy tắc..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button
                variant="primary"
                className="h-14 px-10 rounded-[20px] font-black  tracking-widest gap-2 shadow-xl shadow-blue-500/20"
                onClick={() => setStep(2)}
                disabled={!formData.name}
              >
                Tiếp tục <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            <div>
              <label className="text-[11px] font-black text-slate-400  tracking-[0.15em] ml-1 block mb-4">Chọn sự kiện kích hoạt (Trigger)</label>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(triggerLabels).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setFormData({...formData, trigger_type: key as any})}
                    className={`p-5 rounded-[24px] border-2 text-left transition-all group flex items-start gap-4 ${formData.trigger_type === key ? 'border-blue-600 bg-blue-50 shadow-lg shadow-blue-100' : 'border-slate-50 bg-white hover:border-slate-200'}`}
                  >
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${formData.trigger_type === key ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-400 group-hover:bg-slate-100'}`}>
                      <Zap className="w-6 h-6" />
                    </div>
                    <div>
                      <p className={`text-xs font-black tracking-tight leading-snug ${formData.trigger_type === key ? 'text-blue-900' : 'text-slate-900'}`}>{label}</p>
                      <p className="text-[9px] text-slate-400 font-bold  tracking-widest mt-1">Automation Event</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t border-slate-50">
              <Button variant="ghost" className="h-14 px-8 rounded-[20px] font-bold" onClick={() => setStep(1)}>Quay lại</Button>
              <Button
                variant="primary"
                className="h-14 px-10 rounded-[20px] font-black  tracking-widest gap-2 shadow-xl shadow-blue-500/20"
                onClick={() => setStep(3)}
                disabled={!formData.trigger_type}
              >
                Tiếp tục <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            <div>
              <label className="text-[11px] font-black text-slate-400  tracking-[0.15em] ml-1 block mb-4">Chọn hành động thực hiện (Action)</label>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(actionLabels).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setFormData({...formData, action_type: key as any})}
                    className={`p-5 rounded-[24px] border-2 text-left transition-all group flex items-start gap-4 ${formData.action_type === key ? 'border-emerald-600 bg-emerald-50 shadow-lg shadow-emerald-100' : 'border-slate-50 bg-white hover:border-slate-200'}`}
                  >
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${formData.action_type === key ? 'bg-emerald-600 text-white' : 'bg-slate-50 text-slate-400 group-hover:bg-slate-100'}`}>
                      <Activity className="w-6 h-6" />
                    </div>
                    <div>
                      <p className={`text-xs font-black tracking-tight leading-snug ${formData.action_type === key ? 'text-emerald-900' : 'text-slate-900'}`}>{label}</p>
                      <p className="text-[9px] text-slate-400 font-bold  tracking-widest mt-1">System Action</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t border-slate-50">
              <Button variant="ghost" className="h-14 px-8 rounded-[20px] font-bold" onClick={() => setStep(2)}>Quay lại</Button>
              <Button
                variant="primary"
                className="h-14 px-10 rounded-[20px] font-black  tracking-widest gap-2 bg-emerald-600 hover:bg-emerald-700 shadow-xl shadow-emerald-500/20"
                onClick={handleSave}
                disabled={!formData.action_type}
              >
                Hoàn tất & Lưu <Plus className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Placeholder icons if Lucide components are not available directly in this context
const Clock = (props: any) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const Users = (props: any) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
